import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Layers, Search, CheckCircle, XCircle, AlertTriangle,
  ExternalLink, Copy, Download, Smartphone, Globe,
  Package, ChevronDown, ChevronRight, Users, Star, Tag, X
} from 'lucide-react';

// ─── Platform badge ───────────────────────────────────────────────────────────
// ── CSV column picker — shared by both tabs; selection remembered per tab ────
function CsvColumnPicker({ title, columns, storageKey, onExport, onClose }) {
  const [selected, setSelected] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (Array.isArray(saved) && saved.length) {
        const valid = saved.filter(k => columns.some(c => c.key === k));
        if (valid.length) return new Set(valid);
      }
    } catch {}
    return new Set(columns.map(c => c.key));
  });

  function toggle(key) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function doExport() {
    if (selected.size === 0) return toast.error('Select at least one column');
    const keys = columns.filter(c => selected.has(c.key)).map(c => c.key);
    try { localStorage.setItem(storageKey, JSON.stringify(keys)); } catch {}
    onExport(keys);
    onClose();
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={onClose}>
      <div className="card" style={{ width:'380px', maxHeight:'80vh', overflowY:'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
          <div className="card-title"><Download size={14}/> {title}</div>
          <button onClick={onClose} aria-label="Close"
            style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:'2px' }}>
            <X size={14}/>
          </button>
        </div>
        <div style={{ fontSize:'11px', color:'var(--text-muted)', marginBottom:'10px' }}>
          Choose the columns for your CSV — your selection is remembered for next time.
        </div>
        <div style={{ display:'flex', gap:'8px', alignItems:'center', marginBottom:'10px' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setSelected(new Set(columns.map(c => c.key)))}>Select all</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setSelected(new Set())}>Clear</button>
          <span style={{ marginLeft:'auto', fontSize:'10px', color:'var(--text-muted)' }}>{selected.size}/{columns.length}</span>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:'2px', marginBottom:'14px' }}>
          {columns.map(c => (
            <label key={c.key} style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'12px', color:'var(--text-primary)', cursor:'pointer', padding:'5px 8px', borderRadius:'var(--radius-sm)', background: selected.has(c.key) ? 'var(--bg-elevated)' : 'transparent' }}>
              <input type="checkbox" checked={selected.has(c.key)} onChange={() => toggle(c.key)} style={{ accentColor:'var(--cyan)', cursor:'pointer' }}/>
              {c.label}
            </label>
          ))}
        </div>
        <button className="btn btn-primary w-full" onClick={doExport}>
          <Download size={13}/> Export {selected.size === columns.length ? 'all columns' : `${selected.size} column${selected.size !== 1 ? 's' : ''}`}
        </button>
      </div>
    </div>
  );
}

// Shared CSV download helper
function downloadCsv(rows, filename) {
  const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = filename;
  a.click();
  toast.success('CSV downloaded');
}

function PlatformBadge({ platform }) {
  if (platform === 'ios') return <span className="badge badge-cyan">iOS / App Store</span>;
  if (platform === 'android') return <span className="badge badge-green">Android / Google Play</span>;
  return <span className="badge badge-muted">Unknown</span>;
}

// ─── DSP status pills ─────────────────────────────────────────────────────────
function DspStatus({ dspStatus }) {
  if (!dspStatus) return null;
  const { approvedAcross = [], found } = dspStatus;
  if (!found && approvedAcross.length === 0) {
    return <span className="badge badge-muted">Not in any DSP list</span>;
  }
  return (
    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
      {approvedAcross.map((name, i) => (
        <span key={i} className="badge badge-green"><CheckCircle size={9} /> {name}</span>
      ))}
    </div>
  );
}

// ─── Result card ──────────────────────────────────────────────────────────────
function ResultCard({ result }) {
  function copyText(text) {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  }

  if (!result.success) {
    return (
      <div style={{
        background: 'var(--bg-card)', border: '1px solid color-mix(in srgb, var(--red) 30%, transparent)',
        borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: '8px',
        display: 'flex', gap: '12px', alignItems: 'flex-start'
      }}>
        <XCircle size={16} style={{ color: 'var(--red)', marginTop: '2px', flexShrink: 0 }} />
        <div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '13px', color: 'var(--text-primary)', marginBottom: '4px' }}>
            {result.bundleId}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--red)' }}>{result.error}</div>
          <div style={{ marginTop: '4px' }}><PlatformBadge platform={result.platform} /></div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)', marginBottom: '10px', overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', gap: '14px', padding: '14px 16px', alignItems: 'flex-start',
        borderBottom: '1px solid var(--border)', background: 'var(--bg-elevated)'
      }}>
        {/* App icon */}
        {result.iconUrl ? (
          <img src={result.iconUrl} alt={result.appName}
            style={{ width: 52, height: 52, borderRadius: '12px', objectFit: 'cover', flexShrink: 0, border: '1px solid var(--border)' }} />
        ) : (
          <div style={{ width: 52, height: 52, borderRadius: '12px', background: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid var(--border)' }}>
            <Smartphone size={22} style={{ color: 'var(--text-muted)' }} />
          </div>
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '2px' }}>
                {result.appName || 'Unknown App'}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '6px' }}>
                {result.developerName || '—'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <PlatformBadge platform={result.platform} />
              {result.price && <span className="badge badge-muted">{result.price}</span>}
              {result.rating && <span className="badge badge-amber">★ {result.rating}</span>}
              {result.primaryGenre && <span className="badge badge-muted">{result.primaryGenre}</span>}
            </div>
          </div>

          {/* Bundle ID */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
            <code style={{ fontSize: '11px', color: 'var(--cyan)', fontFamily: 'var(--font-mono)', background: 'var(--bg-base)', padding: '2px 8px', borderRadius: '3px', border: '1px solid var(--border)' }}>
              {result.bundleId}
            </code>
            <button onClick={() => copyText(result.bundleId)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px' }}>
              <Copy size={11} />
            </button>
          </div>
        </div>
      </div>

      {/* Details grid */}
      <div style={{ padding: '14px 16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0' }}>

          {/* Store URL */}
          {result.storeUrl && (
            <div style={{ padding: '7px 0', borderBottom: '1px solid var(--border)', gridColumn: '1 / -1', display: 'flex', gap: '12px', alignItems: 'center' }}>
              <span style={{ minWidth: '160px', fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0 }}>Store URL</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {result.storeUrl}
                </span>
                <a href={result.storeUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--cyan)', flexShrink: 0 }}>
                  <ExternalLink size={12} />
                </a>
                <button onClick={() => copyText(result.storeUrl)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px', flexShrink: 0 }}>
                  <Copy size={11} />
                </button>
              </div>
            </div>
          )}

          {/* Developer Domain */}
          <div style={{ padding: '7px 0', borderBottom: '1px solid var(--border)', gridColumn: '1 / -1', display: 'flex', gap: '12px', alignItems: 'center' }}>
            <span style={{ minWidth: '160px', fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0 }}>Developer Domain</span>
            {result.developerDomain ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <code style={{ fontSize: '12px', color: 'var(--green)', fontFamily: 'var(--font-mono)' }}>{result.developerDomain}</code>
                <button onClick={() => copyText(result.developerDomain)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px' }}>
                  <Copy size={11} />
                </button>
              </div>
            ) : (
              <span style={{ fontSize: '12px', color: 'var(--amber)' }}>
                <AlertTriangle size={11} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                Not found — developer may not have a website listed
              </span>
            )}
          </div>

          {/* App-ads.txt URL (if developer domain found) */}
          {result.developerDomain && (
            <div style={{ padding: '7px 0', borderBottom: '1px solid var(--border)', gridColumn: '1 / -1', display: 'flex', gap: '12px', alignItems: 'center' }}>
              <span style={{ minWidth: '160px', fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0 }}>app-ads.txt URL</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <code style={{ fontSize: '12px', color: 'var(--cyan)', fontFamily: 'var(--font-mono)' }}>
                  https://{result.developerDomain}/app-ads.txt
                </code>
                <a href={`https://${result.developerDomain}/app-ads.txt`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--cyan)' }}>
                  <ExternalLink size={12} />
                </a>
                <button onClick={() => copyText(`https://${result.developerDomain}/app-ads.txt`)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px' }}>
                  <Copy size={11} />
                </button>
              </div>
            </div>
          )}

          {/* Developer Name */}
          {result.developerName && (
            <div style={{ padding: '7px 0', borderBottom: '1px solid var(--border)', display: 'flex', gap: '12px' }}>
              <span style={{ minWidth: '160px', fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0 }}>Developer</span>
              <span style={{ fontSize: '12px', color: 'var(--text-primary)' }}>{result.developerName}</span>
            </div>
          )}

          {/* Version */}
          {result.version && (
            <div style={{ padding: '7px 0', borderBottom: '1px solid var(--border)', display: 'flex', gap: '12px' }}>
              <span style={{ minWidth: '160px', fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0 }}>Version</span>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{result.version}</span>
            </div>
          )}

          {/* Release date */}
          {result.releaseDate && (
            <div style={{ padding: '7px 0', borderBottom: '1px solid var(--border)', display: 'flex', gap: '12px' }}>
              <span style={{ minWidth: '160px', fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0 }}>Released</span>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{new Date(result.releaseDate).toLocaleDateString()}</span>
            </div>
          )}

          {/* Ratings */}
          {result.ratingCount && (
            <div style={{ padding: '7px 0', borderBottom: '1px solid var(--border)', display: 'flex', gap: '12px' }}>
              <span style={{ minWidth: '160px', fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0 }}>Ratings</span>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{result.ratingCount.toLocaleString()} ratings</span>
            </div>
          )}

          {/* DSP Status */}
          <div style={{ padding: '7px 0', gridColumn: '1 / -1', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <span style={{ minWidth: '160px', fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0, marginTop: '2px' }}>DSP Status</span>
            <DspStatus dspStatus={result.dspStatus} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

// ─── Developer Catalog Tab ────────────────────────────────────────────────────
function DeveloperCatalogTab() {
  const [urlInput, setUrlInput]     = useState('');
  const [loading, setLoading]       = useState(false);
  const [results, setResults]       = useState(null);
  const [expanded, setExpanded]     = useState({});
  const [error, setError]           = useState('');
  const PLACEHOLDER = [
    'Paste one or more developer store page URLs — one per line:',
    '',
    'Android: https://play.google.com/store/apps/dev?id=5889854114365514439',
    'iOS:     https://apps.apple.com/in/developer/zynga-inc/id295913422',
  ].join('\n');

  async function handleFetch() {
    const urls = urlInput.split('\n').map(l => l.trim()).filter(Boolean);
    if (urls.length === 0) return toast.error('Enter at least one developer store URL');
    if (urls.length > 20) return toast.error('Maximum 20 developer URLs at once');
    setLoading(true); setResults(null); setError('');
    try {
      const { data } = await axios.post('/api/bundle-lookup/dev-lookup', { devUrls: urls });
      setResults(data);
      // Expand all by default if only 1 developer
      if (data.developers.length === 1) {
        setExpanded({ 0: true });
      }
      toast.success(`Found ${data.totalApps} apps across ${data.developers.length} developer${data.developers.length !== 1 ? 's' : ''}`);
    } catch (e) {
      const msg = e.response?.data?.error || 'Lookup failed';
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  }

  function toggleExpand(i) {
    setExpanded(prev => ({ ...prev, [i]: !prev[i] }));
  }

  const DEV_CSV_COLUMNS = [
    { key:'developer',    label:'Developer',            get:(dev, app) => dev.devName || '' },
    { key:'platform',     label:'Platform',             get:(dev, app) => app.platform || '' },
    { key:'bundleId',     label:'Bundle ID',            get:(dev, app) => app.bundleId || '' },
    { key:'bundleIdent',  label:'iOS Bundle Identifier', get:(dev, app) => app.bundleIdentifier || '' },
    { key:'appName',      label:'App Name',             get:(dev, app) => app.appName || '' },
    { key:'genre',        label:'Genre',                get:(dev, app) => app.primaryGenre || '' },
    { key:'rating',       label:'Rating',               get:(dev, app) => app.rating || '' },
    { key:'ratingCount',  label:'Ratings Count',        get:(dev, app) => app.ratingCount || '' },
    { key:'price',        label:'Price',                get:(dev, app) => app.price || '' },
    { key:'storeUrl',     label:'Store URL',            get:(dev, app) => app.storeUrl || '' },
    { key:'devDomain',    label:'Developer Domain',     get:(dev, app) => app.developerDomain || dev.developerDomain || '' },
    { key:'appAdsTxt',    label:'app-ads.txt URL',      get:(dev, app) => (app.developerDomain || dev.developerDomain) ? `https://${app.developerDomain || dev.developerDomain}/app-ads.txt` : '' },
    { key:'releaseDate',  label:'Release Date',         get:(dev, app) => app.releaseDate || '' },
    { key:'version',      label:'Version',              get:(dev, app) => app.version || '' },
  ];
  const [showCsvPicker, setShowCsvPicker] = useState(false);

  function exportCSV(keys) {
    if (!results) return;
    const cols = DEV_CSV_COLUMNS.filter(c => keys.includes(c.key));
    const rows = [cols.map(c => c.label)];
    results.developers.forEach(dev => {
      (dev.apps || []).forEach(app => {
        rows.push(cols.map(c => c.get(dev, app)));
      });
    });
    downloadCsv(rows, 'developer-catalog.csv');
  }

  const PLAT_COLOR = { ios: 'var(--cyan)', android: 'var(--green)' };

  return (
    <div>
      {/* Input card */}
      <div className="card" style={{ marginBottom:'16px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
          <div className="card-title"><Users size={14}/> Developer Store URLs</div>
          <span className="badge badge-muted" style={{ fontSize:'10px' }}>Max 20 URLs</span>
        </div>
        <textarea className="input-field w-full"
          style={{ minHeight:'140px', fontSize:'12px', lineHeight:1.6 }}
          placeholder={PLACEHOLDER}
          value={urlInput}
          onChange={e => setUrlInput(e.target.value)}
        />
        <div style={{ display:'flex', gap:'8px', marginTop:'10px', alignItems:'center', flexWrap:'wrap' }}>
          <button className="btn btn-primary" onClick={handleFetch} disabled={loading} style={{ minWidth:'160px' }}>
            {loading
              ? <><span className="spinner"/> Fetching apps...</>
              : <><Search size={14}/> Fetch Developer Apps</>}
          </button>
          {urlInput && (
            <span style={{ fontSize:'11px', color:'var(--text-muted)' }}>
              {urlInput.split('\n').filter(l => l.trim()).length} URL{urlInput.split('\n').filter(l => l.trim()).length !== 1 ? 's' : ''}
            </span>
          )}
          {results && (
            <button className="btn btn-secondary btn-sm" onClick={() => setShowCsvPicker(true)} style={{ marginLeft:'auto' }}>
              <Download size={12}/> Export CSV ({results.totalApps} apps)
            </button>
          )}
          {showCsvPicker && (
            <CsvColumnPicker
              title="Export Developer Catalog"
              columns={DEV_CSV_COLUMNS}
              storageKey="bl_csv_cols_catalog"
              onExport={exportCSV}
              onClose={() => setShowCsvPicker(false)}
            />
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-error" style={{ marginBottom:'12px' }}>
          <AlertTriangle size={13}/> {error}
        </div>
      )}

      {/* Summary stat bar */}
      {results && (
        <div className="grid-4 stat-grid-compact" style={{ marginBottom:'16px' }}>
          <div className="stat-card cyan"><div className="stat-label">Developers</div><div className="stat-value">{results.developers.length}</div></div>
          <div className="stat-card green"><div className="stat-label">Total Apps</div><div className="stat-value green">{results.totalApps}</div></div>
          <div className="stat-card cyan"><div className="stat-label">iOS Apps</div><div className="stat-value">{results.developers.reduce((s,d) => s + (d.apps||[]).filter(a=>a.platform==='ios').length, 0)}</div></div>
          <div className="stat-card green"><div className="stat-label">Android Apps</div><div className="stat-value">{results.developers.reduce((s,d) => s + (d.apps||[]).filter(a=>a.platform==='android').length, 0)}</div></div>
        </div>
      )}

      {/* Developer cards */}
      {results && results.developers.map((dev, di) => (
        <div key={di} className="card" style={{ marginBottom:'12px', padding:0, overflow:'hidden' }}>

          {/* Developer header — clickable to expand/collapse */}
          <div onClick={() => toggleExpand(di)}
            style={{ display:'flex', alignItems:'center', gap:'12px', padding:'14px 18px',
              cursor:'pointer', background:'var(--bg-elevated)',
              borderBottom: expanded[di] ? '1px solid var(--border)' : 'none',
              userSelect:'none' }}>
            {/* Expand icon */}
            {expanded[di]
              ? <ChevronDown size={14} style={{ color:'var(--text-muted)', flexShrink:0 }}/>
              : <ChevronRight size={14} style={{ color:'var(--text-muted)', flexShrink:0 }}/>}

            {/* Platform badge */}
            <span className={`badge ${dev.platform === 'ios' ? 'badge-cyan' : 'badge-green'}`} style={{ flexShrink:0 }}>
              {dev.platform === 'ios' ? 'iOS' : 'Android'}
            </span>

            {/* Dev name */}
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontWeight:700, fontSize:'14px', color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {dev.error ? <span style={{ color:'var(--red)' }}>Error: {dev.error}</span> : dev.devName}
              </div>
              {dev.developerDomain && (
                <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'2px' }}>
                  {dev.developerDomain} &nbsp;·&nbsp;
                  <a href={`https://${dev.developerDomain}/app-ads.txt`} target="_blank" rel="noopener noreferrer"
                    style={{ color:'var(--cyan)', textDecoration:'none' }} onClick={e => e.stopPropagation()}>
                    app-ads.txt
                  </a>
                </div>
              )}
            </div>

            {/* App count badge */}
            <span className="badge badge-muted" style={{ flexShrink:0, fontSize:'11px' }}>
              {dev.appCount || (dev.apps?.length || 0)} app{(dev.appCount || dev.apps?.length || 0) !== 1 ? 's' : ''}
            </span>


            {/* Source URL */}
            <a href={dev.url} target="_blank" rel="noopener noreferrer"
              style={{ color:'var(--text-muted)', flexShrink:0 }} onClick={e => e.stopPropagation()}>
              <ExternalLink size={13}/>
            </a>
          </div>

          {/* App list — shown when expanded */}
          {expanded[di] && (dev.apps?.length > 0) && (
            <div style={{ overflowX:'auto' }}>
              <table className="data-table" style={{ minWidth:'800px' }}>
                <thead>
                  <tr>
                    <th style={{ width:'44px' }}></th>
                    <th>App Name</th>
                    <th>Bundle ID</th>
                    <th>Genre</th>
                    <th>Rating</th>
                    <th>Price</th>
                    <th>Dev Domain</th>
                    <th>Links</th>
                  </tr>
                </thead>
                <tbody>
                  {dev.apps.map((app, ai) => (
                    <tr key={ai}>
                      {/* Icon */}
                      <td style={{ padding:'6px 8px', textAlign:'center' }}>
                        {app.iconUrl
                          ? <img src={app.iconUrl} alt="" style={{ width:32, height:32, borderRadius:'7px', display:'block', margin:'0 auto' }}/>
                          : <div style={{ width:32, height:32, borderRadius:'7px', background:'var(--bg-elevated)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto' }}><Package size={14} style={{ color:'var(--text-muted)' }}/></div>
                        }
                      </td>
                      {/* App name */}
                      <td style={{ fontSize:'12px', fontWeight:600, color:'var(--text-primary)', maxWidth:'200px' }}>
                        <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{app.appName || '—'}</div>
                        {app.releaseDate && <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'1px' }}>{app.releaseDate}</div>}
                      </td>
                      {/* Bundle ID */}
                      <td style={{ fontFamily:'var(--font-mono)', fontSize:'11px', color:'var(--cyan)' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                          <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'180px', display:'block' }}>{app.bundleId}</span>
                          <button onClick={() => { navigator.clipboard.writeText(app.bundleId); toast.success('Copied!'); }}
                            style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:0, flexShrink:0 }}>
                            <Copy size={10}/>
                          </button>
                        </div>
                        {app.bundleIdentifier && (
                          <div style={{ fontSize:'9px', color:'var(--text-muted)', marginTop:'2px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'190px' }}>
                            {app.bundleIdentifier}
                          </div>
                        )}
                      </td>
                      {/* Genre */}
                      <td style={{ fontSize:'11px', color:'var(--text-secondary)' }}>
                        {app.primaryGenre
                          ? <span className="badge badge-purple" style={{ fontSize:'10px' }}><Tag size={9}/> {app.primaryGenre}</span>
                          : '—'}
                      </td>
                      {/* Rating */}
                      <td style={{ fontSize:'12px', fontFamily:'var(--font-mono)', whiteSpace:'nowrap' }}>
                        {app.rating
                          ? <span style={{ color:'var(--amber)' }}><Star size={10} style={{ marginRight:'3px', verticalAlign:'middle' }}/>{app.rating}</span>
                          : '—'}
                        {app.ratingCount
                          ? <div style={{ fontSize:'10px', color:'var(--text-muted)' }}>{Number(app.ratingCount).toLocaleString()}</div>
                          : null}
                      </td>
                      {/* Price */}
                      <td style={{ fontSize:'11px', color: app.price && app.price !== 'Free' ? 'var(--amber)' : 'var(--green)' }}>
                        {app.price || 'Free'}
                      </td>
                      {/* Dev domain */}
                      <td style={{ fontSize:'11px', fontFamily:'var(--font-mono)', color:'var(--text-muted)' }}>
                        {(app.developerDomain || dev.developerDomain)
                          ? <a href={`https://${app.developerDomain || dev.developerDomain}/app-ads.txt`} target="_blank" rel="noopener noreferrer"
                              style={{ color:'var(--cyan)', textDecoration:'none' }}>
                              {app.developerDomain || dev.developerDomain}
                            </a>
                          : '—'}
                      </td>
                      {/* Links */}
                      <td style={{ whiteSpace:'nowrap' }}>
                        <a href={app.storeUrl} target="_blank" rel="noopener noreferrer"
                          style={{ color:'var(--text-muted)', marginRight:'8px' }} title="View in store">
                          <ExternalLink size={12}/>
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Empty state */}
          {expanded[di] && (!dev.apps || dev.apps.length === 0) && !dev.error && (
            <div style={{ padding:'24px', textAlign:'center', fontSize:'13px', color:'var(--text-muted)' }}>
              No apps found for this developer — the store page may require JavaScript rendering.
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function BundleLookup() {
  const [activeTab, setActiveTab] = useState('lookup');
  // Mobile lookup state
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const [progress, setProgress] = useState(null); // { done, total } during large lookups

  async function handleLookup() {
    const ids = input.split('\n').map(l => l.trim()).filter(Boolean);
    if (ids.length === 0) return toast.error('Enter at least one bundle ID');
    if (ids.length > 1500) return toast.error('Maximum 1500 bundle IDs at once');
    setLoading(true); setResults(null); setProgress({ done: 0, total: ids.length });
    try {
      // Large lists go up in chunks so each request stays fast; results merge
      // into one combined set with live progress on the button.
      const CHUNK = 150;
      let all = [];
      for (let i = 0; i < ids.length; i += CHUNK) {
        const { data } = await axios.post('/api/bundle-lookup/lookup', { bundleIds: ids.slice(i, i + CHUNK) });
        all = all.concat(data.results || []);
        setProgress({ done: Math.min(i + CHUNK, ids.length), total: ids.length });
      }
      const summary = {
        total: all.length,
        found: all.filter(r => r.success).length,
        notFound: all.filter(r => !r.success).length,
        ios: all.filter(r => r.platform === 'ios' && r.success).length,
        android: all.filter(r => r.platform === 'android' && r.success).length,
        iosNotFound: all.filter(r => r.platform === 'ios' && !r.success).length,
        androidNotFound: all.filter(r => r.platform === 'android' && !r.success).length,
      };
      setResults({ summary, results: all });
      toast.success(`Found ${summary.found} of ${summary.total} apps`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Lookup failed');
    } finally { setLoading(false); setProgress(null); }
  }

  const LOOKUP_CSV_COLUMNS = [
    { key:'bundleId',    label:'Bundle ID',        get:r => r.bundleId },
    { key:'platform',    label:'Platform',         get:r => r.platform || '' },
    { key:'appName',     label:'App Name',         get:r => r.appName || '' },
    { key:'developer',   label:'Developer',        get:r => r.developerName || '' },
    { key:'category',    label:'App Category',     get:r => r.primaryGenre || '' },
    { key:'rating',      label:'Rating',           get:r => r.rating || '' },
    { key:'ratingCount', label:'Ratings Count',    get:r => r.ratingCount || '' },
    { key:'storeUrl',    label:'Store URL',        get:r => r.storeUrl || '' },
    { key:'devDomain',   label:'Developer Domain', get:r => r.developerDomain || '' },
    { key:'appAdsTxt',   label:'app-ads.txt URL',  get:r => r.developerDomain ? `https://${r.developerDomain}/app-ads.txt` : '' },
    { key:'dsp',         label:'DSP Approved',     get:r => r.dspStatus?.approvedAcross?.join(' | ') || '' },
    { key:'error',       label:'Error',            get:r => r.error || '' },
  ];
  const [showCsvPicker, setShowCsvPicker] = useState(false);

  function exportCsv(keys) {
    if (!results) return;
    const cols = LOOKUP_CSV_COLUMNS.filter(c => keys.includes(c.key));
    const rows = [cols.map(c => c.label), ...results.results.map(r => cols.map(c => c.get(r)))];
    downloadCsv(rows, 'bundle-lookup.csv');
  }

  return (
    <div>
      <h1 className="page-title">Bundle Lookup</h1>
      <p className="page-subtitle">Look up app details from bundle IDs, or browse every app in a developer&apos;s portfolio directly from their store page.</p>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom:'16px' }}>
        <button className={`tab ${activeTab==='lookup'?'active':''}`} onClick={()=>setActiveTab('lookup')}>
          <Search size={13} style={{ verticalAlign:'middle', marginRight:'5px' }}/>
          Bundle ID Lookup
        </button>
        <button className={`tab ${activeTab==='catalog'?'active':''}`} onClick={()=>setActiveTab('catalog')}>
          <Users size={13} style={{ verticalAlign:'middle', marginRight:'5px' }}/>
          Developer Catalog
          <span style={{ marginLeft:'6px', fontSize:'9px', fontWeight:800, background:'color-mix(in srgb, var(--amber) 20%, transparent)', color:'var(--amber)', border:'1px solid color-mix(in srgb, var(--amber) 40%, transparent)', borderRadius:'3px', padding:'1px 5px', verticalAlign:'middle' }}>BETA</span>
        </button>
      </div>

      {activeTab === 'lookup' && (<>

      {/* Input */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div className="card-title"><Layers size={14} /> Bundle IDs</div>
          <span className="badge badge-muted" style={{ fontSize: '10px' }}>Max 1500 per lookup</span>
        </div>

        <textarea
          className="input-field w-full"
          style={{ minHeight: '160px', fontSize: '12px' }}
          placeholder={
            'Enter one bundle ID per line:\n\n' +
            'iOS (numeric):      1473573813\n' +
            'Android (reverse):  com.king.candycrushsaga\n' +
            'Android:            com.supercell.clashofclans'
          }
          value={input}
          onChange={e => setInput(e.target.value)}
        />

        <div style={{ display: 'flex', gap: '8px', marginTop: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={handleLookup} disabled={loading} style={{ minWidth: '130px' }}>
            {loading ? <><span className="spinner" /> Looking up{progress && progress.total > 150 ? ` — ${progress.done} / ${progress.total}` : '...'}</> : <><Search size={14} /> Look Up Apps</>}
          </button>
          {input && (
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              {input.split('\n').filter(l => l.trim()).length} bundle ID{input.split('\n').filter(l => l.trim()).length !== 1 ? 's' : ''}
            </span>
          )}
          {results && (
            <button className="btn btn-secondary btn-sm" onClick={() => setShowCsvPicker(true)} style={{ marginLeft: 'auto' }}>
              <Download size={12} /> Export CSV
            </button>
          )}
          {showCsvPicker && (
            <CsvColumnPicker
              title="Export Lookup Results"
              columns={LOOKUP_CSV_COLUMNS}
              storageKey="bl_csv_cols_lookup"
              onExport={exportCsv}
              onClose={() => setShowCsvPicker(false)}
            />
          )}
        </div>
      </div>

      {/* Mobile Results */}
      {results && (
        <>
          <div className="stat-grid-6 stat-grid-compact" style={{ marginBottom: '16px' }}>
            <div className="stat-card cyan"><div className="stat-label">Total</div><div className="stat-value">{results.summary.total}</div></div>
            <div className="stat-card green"><div className="stat-label">Found</div><div className="stat-value green">{results.summary.found}</div></div>
            <div className="stat-card cyan"><div className="stat-label">iOS Apps</div><div className="stat-value">{results.summary.ios}</div></div>
            <div className="stat-card green"><div className="stat-label">Android Apps</div><div className="stat-value">{results.summary.android}</div></div>
            <div className="stat-card red"><div className="stat-label">iOS Not Found</div><div className="stat-value red">{results.summary.iosNotFound ?? 0}</div></div>
            <div className="stat-card red"><div className="stat-label">Android Not Found</div><div className="stat-value red">{results.summary.androidNotFound ?? 0}</div></div>
          </div>
          {/* Upfront notice for large lookups */}
          {results.results.length > 200 && (
            <div className="card" style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 14px', marginBottom:'10px' }}>
              <Download size={13} style={{ color:'var(--cyan)', flexShrink:0 }}/>
              <span style={{ fontSize:'11px', color:'var(--text-secondary)', flex:1 }}>
                Previewing the first <strong style={{ color:'var(--text-primary)' }}>200</strong> of{' '}
                <strong style={{ color:'var(--text-primary)' }}>{results.results.length}</strong> results — export the CSV for your complete data set.
              </span>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowCsvPicker(true)} style={{ flexShrink:0 }}>
                <Download size={11}/> Export full CSV
              </button>
            </div>
          )}

          {/* Render a fast preview for large lookups — the CSV export always
              contains the complete result set */}
          {results.results.slice(0, 200).map((r, i) => <ResultCard key={i} result={r} />)}
          {results.results.length > 200 && (
            <div className="card" style={{ textAlign:'center', padding:'20px', marginTop:'10px' }}>
              <div style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)', marginBottom:'4px' }}>
                Showing the first 200 of {results.results.length} results
              </div>
              <div style={{ fontSize:'11px', color:'var(--text-muted)', marginBottom:'12px' }}>
                Large lookups are previewed to keep the dashboard fast — export the CSV for your complete data set.
              </div>
              <button className="btn btn-primary" onClick={() => setShowCsvPicker(true)}>
                <Download size={13}/> Export full CSV ({results.results.length} rows)
              </button>
            </div>
          )}
        </>
      )}
      </>)}

      {activeTab === 'catalog' && <DeveloperCatalogTab />}
    </div>
  );
}
